import { useEffect, useRef, useState } from 'react';

export default function AdditionalServices() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} className="py-16 md:py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-0 w-48 md:w-64 h-48 md:h-64 bg-[#FF6B6B]/5 rounded-full filter blur-3xl -translate-y-1/2"></div>
      <div className="absolute top-1/2 right-0 w-48 md:w-64 h-48 md:h-64 bg-[#0A1628]/5 rounded-full filter blur-3xl -translate-y-1/2"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-20 relative z-10">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-2 bg-[#0A1628]/5 text-[#0A1628] px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full"></span>
            Additional services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-[#0A1628] mb-4">
            Need <span className="text-[#FF6B6B] italic">more</span>?
          </h2>
          <div className="w-20 md:w-24 h-1 bg-[#FF6B6B] mx-auto rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
          <div 
            className={`group border-2 border-[#0A1628] rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 hover:-translate-y-3 hover:shadow-2xl transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF6B6B]/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
              <i className="ri-settings-3-line text-4xl md:text-5xl text-[#FF6B6B]"></i>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-3 md:mb-4">
              Website Management
            </h3>
            <p className="text-[#0A1628]/70 text-base md:text-lg mb-4 md:mb-6 leading-relaxed">
              Maintenance, content updates, modifications and technical support to keep your site always up to date
            </p>
            <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
              {['Regular updates', 'Content modifications', 'Technical support'].map((item, i) => (
                <li key={i} className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-[#0A1628]/60">
                  <i className="ri-check-line text-[#FF6B6B]"></i>
                  {item}
                </li>
              ))}
            </ul>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-[#FF6B6B] text-sm md:text-base font-semibold flex items-center gap-2 group-hover:gap-4 transition-all whitespace-nowrap cursor-pointer"
            >
              Contact me for a quote
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          <div 
            className={`group border-2 border-[#0A1628] rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 hover:-translate-y-3 hover:shadow-2xl transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#0A1628]/10 to-[#0A1628]/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300">
              <i className="ri-instagram-line text-4xl md:text-5xl text-[#0A1628]"></i>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-3 md:mb-4">
              Social Media Management
            </h3>
            <p className="text-[#0A1628]/70 text-base md:text-lg mb-4 md:mb-6 leading-relaxed">
              Content creation, post scheduling and social media management to grow your online presence
            </p>
            <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
              {['Visual creation', 'Post scheduling', 'Content strategy'].map((item, i) => (
                <li key={i} className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-[#0A1628]/60">
                  <i className="ri-check-line text-[#FF6B6B]"></i>
                  {item}
                </li>
              ))}
            </ul>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-[#FF6B6B] text-sm md:text-base font-semibold flex items-center gap-2 group-hover:gap-4 transition-all whitespace-nowrap cursor-pointer"
            >
              Let's discuss your needs
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
